import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEstablishment } from '../../contexts/EstablishmentContext';
import { FcMenu, FcCancel } from 'react-icons/fc';
import { FaBell } from 'react-icons/fa';
import SearchInput from '../SearchInput';
import CondominiumSelector from '../ui/CondominiumSelector';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import logo from '../../assets/logo.png';
import {
  useMarkNotificationAsRead,
  useNotifications,
  useUnreadNotificationsCount,
} from '../../hooks/useNotifications';
import type { NotificationRecord } from '../../services/api/notifications.service';
import { getNotificationTargetPath } from './notificationTarget';

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const SETTINGS_NOTIFICATIONS_PERMISSION = 'settings.notifications.view';
const TOPBAR_NOTIFICATIONS_LIMIT = 5;

const formatNotificationTimestamp = (createdAt: string): string => {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Header: React.FC<HeaderProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, permissions } = useAuth();
  const { getFormattedRoles } = useEstablishment();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);

  // Hide condominium selector on admin configuration pages (these pages manage ALL condominiums)
  const isAdminConfigPage = location.pathname.startsWith('/configuracoes-administrativas');
  const canAccessNotifications = permissions.includes(
    SETTINGS_NOTIFICATIONS_PERMISSION,
  );

  const { data: notificationsData, isLoading: notificationsLoading } = useNotifications(
    {
      page: 1,
      limit: TOPBAR_NOTIFICATIONS_LIMIT,
    },
    {
      enabled: canAccessNotifications,
      refetchInterval: 30000,
    },
  );
  const { data: unreadData } = useUnreadNotificationsCount({
    enabled: canAccessNotifications,
    refetchInterval: 30000,
  });
  const markNotificationAsReadMutation = useMarkNotificationAsRead();

  const notifications = useMemo(
    () => notificationsData?.data ?? [],
    [notificationsData],
  );
  const unreadCount = unreadData?.count ?? 0;

  useEffect(() => {
    setIsNotificationsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isNotificationsOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!notificationsRef.current || !(target instanceof Node)) {
        return;
      }
      if (!notificationsRef.current.contains(target)) {
        setIsNotificationsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isNotificationsOpen]);

  const openNotificationsCenter = () => {
    setIsNotificationsOpen(false);
    navigate('/configuracoes/notificacoes-e-preferencias');
  };

  const handleNotificationClick = async (notification: NotificationRecord) => {
    if (!notification.readAt) {
      try {
        await markNotificationAsReadMutation.mutate(notification.id);
      } catch {
        return;
      }
    }

    const targetPath = getNotificationTargetPath(notification, permissions);
    if (!targetPath) {
      return;
    }

    setIsNotificationsOpen(false);
    navigate(targetPath);
  };

  return (
    <>
      <header className="bg-white shadow-lg z-10 h-16">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Seção 1: Hamburguer + Logo + Dashboard */}
            <div className="flex items-center flex-shrink-0">
              <button
                className="md:hidden mr-3 p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:scale-110 rounded-lg transition-[background-color,color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-button"
                aria-label="Toggle menu"
              >
                {sidebarOpen ? <FcCancel className="h-6 w-6" /> : <FcMenu className="h-6 w-6" />}
              </button>
              <button
                className="hidden md:flex mr-3 p-2 min-h-[40px] min-w-[40px] items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:scale-110 rounded-lg transition-[background-color,color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="desktop-menu-button"
                aria-label="Toggle sidebar"
              >
                <FcMenu className="h-6 w-6" />
              </button>
              <div className="hidden md:flex items-center">
                <img src={logo} alt="Logo" data-no-outline className="h-10 w-auto mr-4" />
                <div className="border-l border-gray-300 pl-4">
                  <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
                  <p className="text-sm text-gray-500">Sistema de Gestão</p>
                </div>
              </div>
            </div>

            {/* Seção 2: Barra de navegação (sempre no centro) */}
            <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block">
              <div className="max-w-lg">
                <SearchInput />
              </div>
            </div>

            {/* Seção 3: Seletor de condomínios + Usuário */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* Mobile Search - show SearchInput on mobile too */}
              <div className="md:hidden">
                <SearchInput />
              </div>

              {!isAdminConfigPage && <CondominiumSelector />}
              <LanguageSwitcher />
              {canAccessNotifications && (
                <div className="relative" ref={notificationsRef}>
                  <button
                    className="relative p-2 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-xl hover:bg-gray-100 hover:scale-110 transition-[background-color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
                    onClick={() => setIsNotificationsOpen((open) => !open)}
                    aria-label="Notificações"
                    type="button"
                  >
                    <FaBell className="h-5 w-5 text-gray-600 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:rotate-12" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center tabular-nums">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {isNotificationsOpen && (
                    <div className="absolute right-0 mt-2 w-80 rounded-xl border border-gray-200 bg-white shadow-xl z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">Notificações</p>
                        <p className="text-xs text-gray-500">
                          {unreadCount} não lida(s)
                        </p>
                      </div>

                      <div className="max-h-80 overflow-y-auto">
                        {notificationsLoading ? (
                          <p className="px-4 py-5 text-sm text-gray-500">
                            Carregando notificações...
                          </p>
                        ) : notifications.length === 0 ? (
                          <p className="px-4 py-5 text-sm text-gray-500">
                            Nenhuma notificação recente.
                          </p>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notification) => (
                              <button
                                key={notification.id}
                                type="button"
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex items-start gap-2">
                                  {!notification.readAt && (
                                    <span className="mt-1.5 h-2 w-2 rounded-full bg-indigo-600 flex-shrink-0" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {notification.title}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {notification.content}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-1">
                                      {formatNotificationTimestamp(
                                        notification.createdAt,
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="border-t border-gray-100 p-2">
                        <button
                          type="button"
                          className="w-full rounded-md px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                          onClick={openNotificationsCenter}
                        >
                          Ver mais notificações
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="hidden md:block ml-4 text-left">
                <p className="text-sm font-medium text-gray-700">{user?.name || 'Usuário'}</p>
                <div className="flex items-center space-x-2 text-xs">
                  <span className="flex items-center text-gray-500">
                    {getFormattedRoles()}
                  </span>
                  <span className="text-gray-400">•</span>
                  <button
                    onClick={logout}
                    className="text-gray-500 hover:text-red-600 hover:scale-105 transition-[color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
                  >
                    {t('common.logout')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
